import React from 'react'
import { graphql, useFragment } from 'react-relay/hooks'
import { keyBy } from 'lodash'
import { useDecision } from '@optimizely/react-sdk'
import { FlexRow, Card } from 'elements'
import qbLogo from 'theme/images/qbLogo.svg'
import xeroLogo from 'theme/images/xeroLogo.svg'
import IntegrationBox from './IntegrationBox'
import { useApi, useAuth, useAnalytics, useModals, useMountEffect } from 'hooks'
import useIntegrationDelete from './hooks/useIntegrationDelete'
import SelectConnectionModal from './SelectConnectionModal'

const INTEGRATIONS = [
  { kind: 'intuit', logo: qbLogo },
  { kind: 'xero', logo: xeroLogo, logoWidth: '35%' },
]

export default function Integrations({ company }) {
  const [decision] = useDecision('canCreateXeroReports')
  const xeroEnabled = decision.enabled

  const api = useApi()
  const { currentCompany } = useAuth()
  const analytics = useAnalytics()
  const [deleteIntegration] = useIntegrationDelete()
  const { openModal } = useModals()

  const data = useFragment(
    graphql`
      fragment Integrations_company on Company {
        integrations(first: 100) @connection(key: "Integrations_integrations") {
          edges {
            node {
              id
              rowId
              kind
              config
              status
            }
          }
        }
      }
    `,
    company,
  )

  const integrations = data.integrations.edges.map((edge) => edge.node)

  const integrationsByKind = keyBy(integrations, 'kind')

  //mount effect to avoid hyper-insistent modal if user is trying to close
  useMountEffect(() => {
    const xeroIntegration = integrationsByKind?.xero

    if (xeroIntegration && !xeroIntegration.config.xero.currentTenant) {
      openModal(SelectConnectionModal, { integrationRowId: xeroIntegration.rowId })
    }
  })

  const handleDelete = (integration) => {
    const warning =
      'Are you sure you want to delete this integration? Reports generated from this integration will not be deleted.'

    if (window.confirm(warning)) {
      deleteIntegration({ integrationId: integration.id })
      analytics.track('Integration Removed', { integrationId: integration.rowId })
    }
  }

  const handleConnect = (integration) => {
    const url = api.integration.getConnectUrl(currentCompany.rowId, integration.kind)
    window.location.href = url
  }

  return (
    <Card pl="xxlarge" pt="xxlarge" bg="white" flexWrap="wrap">
      {INTEGRATIONS.map((integration) => {
        //return null for xero box if xero feature not enabled
        if (!xeroEnabled && integration.kind === 'xero') {
          return null
        }

        const userIntegration = integrationsByKind[integration.kind]

        return (
          <FlexRow key={integration.kind} mr="xxlarge" mb="xxlarge">
            <IntegrationBox
              userIntegration={userIntegration}
              logo={integration.logo}
              logoWidth={integration.logoWidth}
              onDelete={() => handleDelete(userIntegration)}
              onConnect={() => handleConnect(integration)}
            />
          </FlexRow>
        )
      })}
    </Card>
  )
}
